// Single source of truth for chart capability/availability, shared by the FE
// ChartConfig UI, the dash-meta page-gen compiler, and the page-gen critic.
// IMPORT-PURITY: this file must import ONLY from within @revelio/core (and
// `import type` from @revelio/data-access). Do NOT add FE imports — there is no
// Nx boundary enforcement in this repo, so a stray FE import would silently pull
// FE runtime into core.
import { DataType, getMetricDataTypeCapability } from '../data-type';
import { isSelectableChartType } from './is-selectable-chart-type';
import {
  getAllowedChartTypesForMetric,
  getAllowedDimsForMetric,
} from './matrix';
import type { ChartType, SelectableChartType } from './types/chart';
import type { EntityType, SecondaryDimensionValues } from './types/dimension';
import { secondaryDimensionOptions } from './types/dimension';
import type { GroupedMetricOption, MetricValues } from './types/metric';
import { groupedMetricOptions } from './types/metric';

const DIVERGING_BAR_METRICS: MetricValues[] = [
  'headcount',
  'overall',
  'sentiment_top_bottom',
  'transitions_inflows_and_outflows',
];

// The volume ("Volume Dynamics") chart exposes exactly one selectable variable —
// `postings_volume` — which itself renders the active/new/removed posting counts
// as a single combined chart. The individual posting metrics are the volume
// chart's internal series, NOT independently selectable variables, so they stay
// out of this list (matches the original FE chart-builder rule).
const VOLUME_METRICS: MetricValues[] = ['postings_volume'];

const GROUPED_BAR_METRICS_BY_ENTITY: Record<EntityType, MetricValues[]> = {
  company: [
    'base_salary',
    'contractor_pay_rate',
    'headcount',
    'posting_salary',
    'postings_active',
    'postings_new',
    'postings_removed',
    'time_to_fill',
    'total_compensation',
    'total_spend_compensation',
    'total_spend_salary',
    'transitions_inflow',
    'transitions_outflow',
  ],
  geography: [
    'base_salary',
    'contractor_pay_rate',
    'headcount',
    'postings_active',
    'total_compensation',
    'total_spend_compensation',
    'total_spend_salary',
    'transitions_inflow',
    'transitions_outflow',
  ],
  role: [
    'headcount',
    'postings_active',
    'total_spend_compensation',
    'total_spend_salary',
    'transitions_inflow',
    'transitions_outflow',
  ],
};

// Primary dimension constraints - which dimensions are available per entity type
const PRIMARY_DIMENSION_TO_SEGMENT_MAPPING: Record<
  EntityType,
  SecondaryDimensionValues[]
> = {
  company: [
    'geography',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
    'timeseries',
  ],
  geography: [
    'rics',
    'company',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
    'timeseries',
  ],
  role: [
    'rics',
    'company',
    'geography',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
    'timeseries',
  ],
};

// Metrics that support secondary dimensions (for filtering)
const SUPPORTED_METRICS: Record<MetricValues, boolean> = {
  headcount: true,
  growth_rate: true,
  hiring_rate: true,
  attrition_rate: true,
  tenure: true,
  salary: true,
  share_of_headcount: false,
  transitions_inflow: true,
  transitions_outflow: true,
  transitions_inflows_and_outflows: true,
  posting_salary: true,
  time_to_fill: true,
  postings_active: true,
  postings_new: true,
  postings_removed: true,
  postings_volume: true,
  total_compensation: true,
  base_salary: true,
  salary_growth: false,
  contractor_pay_rate: true,
  total_spend_salary: true,
  total_spend_compensation: true,
  base_salary_percentiles: true,
  total_compensation_percentiles: true,
  contractor_pay_rate_percentiles: true,
  overall: true,
  sentiment_top_bottom: true,
  business_outlook: true,
  career_growth: true,
  compensation: true,
  benefits: true,
  healthcare: true,
  retirement_pension: true,
  childcare: true,
  education_assistance: true,
  wellness_programs: true,
  work_life_balance: true,
  diversity_and_inclusion: true,
  leadership: true,
  culture: true,
  remote_work: true,
  onboarding: true,
  mentorship: true,
  technology: true,
  promotion_opportunities: true,
  paid_time_off: true,
  transparency: true,
  efficiency_and_bureaucracy: true,
  product_and_service_quality: true,
  prestige: true,
  coworkers: true,
  mission_and_vision: true,
  workplace_facilities: true,
  training: true,
  clients_and_customers: true,
  company_growth: true,
  schedule_flexibility: true,
  job_security: true,
  overtime: true,
  family_support: true,
  commission: true,
  independence: true,
  commute: true,
  safety: true,
  internal_communication: true,
  office_politics: true,
  harassment: true,
  honesty: true,
  ethics: true,
  commitment: true,
  compliance: true,
  integrity: true,
  positive_review: true,
  negative_review: true,
  screener_headcount: true,
  screener_growth: true,
  screener_growth_yoy: true,
  screener_hiring: true,
  screener_attrition: true,
  screener_salary: true,
  screener_tenure: true,
  screener_inflow: true,
  screener_outflow: true,
  td_users: false,
  entity_summary: false,
  demand_supply: false,
  demand_supply_percentile: false,
  wage_pressure: false,
  wage_pressure_percentile: false,
  market_tightness: false,
  market_share: false,
  target_companies_rec: false,
  time_to_fill_percentile: false,
  overview_map: false,
};

const isSupportedMetric = (
  metric: MetricValues | undefined
): metric is MetricValues => !!metric && SUPPORTED_METRICS[metric] === true;

function isMetricAllowedForChartBuilder(
  chartType: SelectableChartType,
  metric: MetricValues,
  entityType: EntityType
): boolean {
  const allowedCharts = getAllowedChartTypesForMetric(metric, entityType);
  if (!allowedCharts.includes(chartType)) return false;
  if (chartType === 'diverging-bar' || chartType === 'diverging-bar-combo')
    return DIVERGING_BAR_METRICS.includes(metric);
  if (chartType === 'grouped-bar')
    return GROUPED_BAR_METRICS_BY_ENTITY[entityType].includes(metric);
  if (chartType === 'volume') return VOLUME_METRICS.includes(metric);
  if (chartType === 'bar')
    return (
      getMetricDataTypeCapability(metric).defaultDataType === DataType.SINGLE
    );
  return true;
}

/** Flat list of metric values valid for a chartType+entityType (backend-friendly). */
export function getAvailableMetricValues(
  entityType: EntityType,
  chartType: ChartType | undefined
): MetricValues[] {
  if (!isSelectableChartType(chartType)) return [];
  if (chartType === 'ai-summary' || chartType === 'gauge') return [];
  const all = groupedMetricOptions.flatMap((g) =>
    g.options.map((o) => o.value)
  );
  return all.filter((m) =>
    isMetricAllowedForChartBuilder(chartType, m, entityType)
  );
}

/** Grouped metric options (FE ChartConfig shape). */
export function getAvailableMetrics({
  entityType,
  chartType,
}: {
  entityType?: EntityType;
  chartType?: ChartType;
}): GroupedMetricOption[] {
  if (!entityType || !isSelectableChartType(chartType)) return [];
  const metrics = getAvailableMetricValues(entityType, chartType);
  if (metrics.length === 0) return [];
  return groupedMetricOptions
    .map((group) => {
      const matched = group.options.filter((o) => metrics.includes(o.value));
      return matched.length === 0 ? null : { ...group, options: matched };
    })
    .filter((g): g is GroupedMetricOption => g !== null);
}

/** Flat list of valid secondary-dimension values. */
export function getAvailableSegmentValues({
  entityType,
  chartType,
  metric,
}: {
  entityType: EntityType;
  chartType?: ChartType;
  metric?: MetricValues;
}): SecondaryDimensionValues[] {
  if (!isSelectableChartType(chartType)) return [];
  if (chartType === 'bar') return [];
  if (!isSupportedMetric(metric)) return [];
  if (
    (metric === 'total_spend_salary' ||
      metric === 'total_spend_compensation') &&
    entityType !== 'company'
  )
    return [];
  const metricDims = getAllowedDimsForMetric(metric, chartType);
  const primaryDims = PRIMARY_DIMENSION_TO_SEGMENT_MAPPING[entityType] ?? [];
  return metricDims.filter((dim) => primaryDims.includes(dim));
}

/** Secondary-dimension options (FE ChartConfig shape). */
export function getAvailableSegment({
  entityType,
  chartType,
  metric,
}: {
  entityType: EntityType;
  chartType?: ChartType;
  metric?: MetricValues;
}) {
  const values = getAvailableSegmentValues({ entityType, chartType, metric });
  return secondaryDimensionOptions.filter((o) => values.includes(o.value));
}

export function getAvailableSegments({
  entityType,
  chartType,
  metrics,
}: {
  entityType: EntityType;
  chartType?: ChartType;
  metrics?: MetricValues[];
}) {
  const perMetric =
    metrics?.map((metric) =>
      getAvailableSegmentValues({ entityType, chartType, metric })
    ) ?? [];
  if (perMetric.length === 0) return [];
  return secondaryDimensionOptions.filter((o) =>
    perMetric.every((vals) => vals.includes(o.value))
  );
}
